import { useEffect, useRef } from 'react';
import { getOrCreateSessionId, getDeviceType } from '../utils/analytics';

// Ganti baris 5 & 6 menjadi:
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

import { supabase } from '../supabaseClient';

let sessionId = '';

/**
 * useAnalytics Hook
 * Tracks user behavior and sends to Supabase
 * 
 * Usage:
 * const { trackPageView, trackFeatureClick, trackMentorInteraction } = useAnalytics();
 */
export const useAnalytics = () => {
    const sessionIdRef = useRef<string>('');

    useEffect(() => {
        if (!sessionIdRef.current) {
            sessionIdRef.current = getOrCreateSessionId();
            sessionId = sessionIdRef.current;
        }
    }, []);

    /**
     * Track page/slide view
     * Call when user navigates to a new slide
     */
    const trackPageView = async (
        slideNumber: number,
        slideName: string,
        viewDurationSeconds: number = 0
    ) => {
        try {
            await supabase.from('page_views').insert({
                user_id: sessionIdRef.current,
                page: slideName,
                viewed_at: new Date().toISOString(),
                device: getDeviceType(),
                metadata: {
                    slide_number: slideNumber,
                    view_duration_seconds: viewDurationSeconds,
                    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : ''
                }
            });
        } catch (err) {
            console.error('[Analytics] Page view error:', err);
        }
    };

    /**
     * Track feature/button clicks
     * Call when user clicks buttons (Mulai Simulasi, Cari Manual, etc)
     */
    const trackFeatureClick = async (
        featureName: string,
        featureType: string,
        slideNumber?: number
    ) => {
        try {
            await supabase.from('feature_clicks').insert({
                user_id: sessionIdRef.current,
                feature: featureName,
                clicked_at: new Date().toISOString(),
                metadata: {
                    feature_type: featureType,
                    slide_number: slideNumber,
                    device: getDeviceType()
                }
            });
        } catch (err) {
            console.error('[Analytics] Feature click error:', err);
        }
    };

    /**
     * Track mentor interactions
     * Call when user clicks on mentor (detail, chat, compare, instagram)
     */
    const trackMentorInteraction = async (
        mentorId: string,
        actionType: string,
        sourceFeature: string,
        sourceSlide?: number
    ) => {
        try {
            await supabase.from('mentor_interactions').insert({
                user_id: sessionIdRef.current,
                mentor_id: mentorId,
                action: actionType,
                interacted_at: new Date().toISOString(),
                metadata: {
                    source_feature: sourceFeature,
                    source_slide: sourceSlide,
                    device: getDeviceType()
                }
            });
        } catch (err) {
            console.error('[Analytics] Mentor interaction error:', err);
        }
    };

    return {
        trackPageView,
        trackFeatureClick,
        trackMentorInteraction,
        sessionId: sessionIdRef.current,
    };
};
